import { type PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type StorageType, getStorageByType } from 'utils/storage';

import type { BaseTapItem } from './model';

type TabListContextProps<T extends BaseTapItem> = {
  tabList: T[];
  onTabClick: (button: T['type']) => void;
  activateTabItem: T | undefined;
};

const TabListContext = createContext<TabListContextProps<BaseTapItem>>({
  tabList: [],
  onTabClick: () => {},
  activateTabItem: undefined,
});

type TabListProviderProps<T extends BaseTapItem> = {
  tabList: T[];
  storage?: {
    type: StorageType;
    name: string;
  };
};
export function TabListProvider<T extends BaseTapItem>({
  children,
  tabList,
  storage,
}: PropsWithChildren<TabListProviderProps<T>>) {
  const { t } = useTranslation();
  const [internalTabList] = useState<T[]>(tabList);
  const [activateTabItem, setActivateTabItem] = useState<T>(() => {
    const defaultValue = internalTabList[0];
    // storage 옵션을 사용하지 않는 경우, 기본값
    const isUnuseStorage = !storage;
    if (isUnuseStorage) return defaultValue;

    try {
      const storageObject = getStorageByType(storage.type);
      const storedData = storageObject.getItem(storage.name);

      return storedData ? (JSON.parse(storedData) as T) : defaultValue;
    } catch (error) {
      console.error(`Failed to parse stored tab state: ${error}`);
      return defaultValue;
    }
  });

  useEffect(() => {
    if (!storage) return;

    try {
      const storageObject = getStorageByType(storage.type);
      storageObject.setItem(storage.name, JSON.stringify(activateTabItem));
    } catch (error) {
      console.error(`Failed to store tab state: ${error}`);
    }
  }, [activateTabItem, storage]);

  const onTabClick = (tab: T['type']) => {
    const foundTab = internalTabList.find((item) => item.type === tab);
    if (!foundTab) {
      return;
    }
    setActivateTabItem(foundTab);
  };

  return (
    <TabListContext.Provider
      value={{ tabList: internalTabList.map((tab) => ({ ...tab, label: t(tab.label) })), onTabClick, activateTabItem }}
    >
      {children}
    </TabListContext.Provider>
  );
}

export const useTabListContext = () => {
  const context = useContext(TabListContext);
  if (!context) {
    throw new Error('TabListContext must be used within a TabListProvider');
  }
  return context;
};
